import { useState, useCallback, useEffect } from 'react'
import { X, Loader2, Briefcase, Building, DollarSign, Calendar } from 'lucide-react'
import { clsx } from 'clsx'
import { useCreateDeal } from '@/hooks/useDeals'
import { useToast } from '@/components'
import { parseApiError, formatErrorMessage } from '@/services/api'

interface CreateDealModalProps {
  isOpen: boolean
  onClose: () => void
}

export default function CreateDealModal({ isOpen, onClose }: CreateDealModalProps) {
  // 表单状态
  const [name, setName] = useState('')
  const [stage, setStage] = useState('')  // 阶段（必填）
  const [customerName, setCustomerName] = useState('')
  const [industry, setIndustry] = useState('')
  const [amountEst, setAmountEst] = useState('')
  const [expectedCloseDate, setExpectedCloseDate] = useState('')

  // Hooks
  const createDeal = useCreateDeal()
  const toast = useToast()

  // 重置表单
  const resetForm = useCallback(() => {
    setName('')
    setStage('')
    setCustomerName('')
    setIndustry('')
    setAmountEst('')
    setExpectedCloseDate('')
  }, [])

  // 关闭弹窗
  const handleClose = useCallback(() => {
    if (!createDeal.isPending) {
      resetForm()
      onClose()
    }
  }, [createDeal.isPending, resetForm, onClose])

  // 提交表单
  const handleSubmit = useCallback(async () => {
    // 前端验证
    if (!name.trim()) {
      toast.error('机会名称不能为空')
      return
    }
    if (!stage) {
      toast.error('请选择项目当前阶段', '阶段是必填项，请根据客户采购流程选择')
      return
    }

    try {
      const dealData: {
        name: string
        stage: string
        customerName?: string
        industry?: string
        amountEst?: number
        expectedCloseDate?: string
      } = {
        name: name.trim(),
        stage: stage,
      }

      if (customerName.trim()) {
        dealData.customerName = customerName.trim()
      }
      if (industry.trim()) {
        dealData.industry = industry.trim()
      }
      if (amountEst.trim()) {
        const amount = parseFloat(amountEst)
        if (!isNaN(amount) && amount > 0) {
          dealData.amountEst = amount
        }
      }
      if (expectedCloseDate) {
        dealData.expectedCloseDate = expectedCloseDate
      }

      await createDeal.mutateAsync(dealData)

      // 成功提示
      toast.success(`已创建「${name.trim()}」`, '机会已添加到列表')

      // 关闭弹窗并重置
      resetForm()
      onClose()
    } catch (error) {
      console.error('Failed to create deal:', error)
      const apiError = parseApiError(error)
      const { title, message } = formatErrorMessage(apiError)
      toast.error(title, message)
    }
  }, [name, stage, customerName, industry, amountEst, expectedCloseDate, createDeal, toast, resetForm, onClose])

  // 监听弹窗关闭事件，重置表单
  useEffect(() => {
    if (!isOpen) {
      resetForm()
    }
  }, [isOpen, resetForm])

  // Enter键提交
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !createDeal.isPending && name.trim() && stage) {
        handleSubmit()
      }
    },
    [createDeal.isPending, name, stage, handleSubmit]
  )

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* 背景遮罩 */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* 弹窗内容 */}
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
      >
          {/* 头部 */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">新建销售机会</h2>
                <p className="text-sm text-gray-500">填写基本信息开始跟进</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={createDeal.isPending}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* 表单内容 */}
          <div className="p-6 space-y-4">
            {/* 机会名称（必填） */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                机会名称 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="例如：某银行数字化转型项目"
                disabled={createDeal.isPending}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                autoFocus
              />
            </div>

            {/* 项目当前阶段（必填） */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                项目当前阶段 <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-gray-500 mb-2">
                请根据客户采购流程的客观进度选择
              </p>
              <select
                value={stage}
                onChange={(e) => setStage(e.target.value)}
                disabled={createDeal.isPending}
                className={clsx(
                  "w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500",
                  !stage ? "border-gray-300 text-gray-500" : "border-gray-300 text-gray-900"
                )}
              >
                <option value="">-- 请选择 --</option>
                <option value="lead">线索 - 初步接触，了解客户需求</option>
                <option value="qualify">验证 - 确认机会真实性和预算</option>
                <option value="proposal">方案 - 提交解决方案，POC/试点</option>
                <option value="negotiate">谈判/招投标 - 商务谈判或投标阶段</option>
              </select>
            </div>

            {/* 客户名称 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Building className="w-4 h-4" />
                客户名称
              </label>
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="例如：XX银行"
                disabled={createDeal.isPending}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              />
            </div>

            {/* 客户行业 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                客户行业
              </label>
              <input
                type="text"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="例如：金融、制造、政府等"
                disabled={createDeal.isPending}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              />
            </div>

            {/* 预计金额和日期 */}
            <div className="grid grid-cols-2 gap-4">
              {/* 预计金额 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  预计金额
                </label>
                <input
                  type="number"
                  value={amountEst}
                  onChange={(e) => setAmountEst(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="单位：元"
                  disabled={createDeal.isPending}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                  min="0"
                  step="1000"
                />
              </div>

              {/* 预计成交日期 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  预计成交
                </label>
                <input
                  type="date"
                  value={expectedCloseDate}
                  onChange={(e) => setExpectedCloseDate(e.target.value)}
                  disabled={createDeal.isPending}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
            </div>
          </div>

          {/* 底部操作栏 */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50">
            <button
              onClick={handleClose}
              disabled={createDeal.isPending}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium disabled:opacity-50"
            >
              取消
            </button>
            <button
              onClick={handleSubmit}
              disabled={createDeal.isPending || !name.trim() || !stage}
              className={clsx(
                'px-6 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors',
                createDeal.isPending || !name.trim() || !stage
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-primary-600 text-white hover:bg-primary-700'
              )}
            >
              {createDeal.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              创建机会
            </button>
          </div>
        </div>
      </div>
  )
}

